// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { optionalBankIbanSchema } from '@client/banks/types/account';
import { bankPositionAssetTypeSchema } from '@client/banks/types/position';
import dayjs from 'dayjs';
import { z } from 'zod';

/**
 * Movement product families aligned with aggregation
 * `aggregator.transaction-type.*` i18n keys.
 */
export enum BankTransactionType {
  Account = 'account',
  CreditCard = 'credit_card',
  Portfolio = 'portfolio',
}

export const bankTransactionTypeSchema = z.enum(BankTransactionType);

/**
 * Movement subtypes aligned with aggregation `aggregator.transaction-subtype.*`
 * i18n keys.
 */
export enum BankTransactionSubtype {
  Commission = 'commission',
  Food = 'food',
  Education = 'education',
  Pet = 'pet',
  SmallBusiness = 'small-business',
  Fuel = 'fuel',
  Supplies = 'supplies',
  PhoneInternet = 'phone-internet',
  ServiceSubscription = 'service-subscription',
  Trips = 'trips',
  Restaurants = 'restaurants',
  Health = 'health',
  Taxes = 'taxes',
  AtmOperation = 'atm-operation',
  LoanInstallment = 'loan-installment',
  MicroloanInstallment = 'microloan-installment',
  CashOperation = 'cash-operation',
  CommunityReceipt = 'community-receipt',
  RentReceipt = 'rent-receipt',
  Ngo = 'ngo',
  Payroll = 'payroll',
  OnlineTransactions = 'online-transactions',
  CommissionPayment = 'commission-payment',
  DividendCollection = 'dividend-collection',
  Purchase = 'purchase',
  Sale = 'sale',
  SubscriptionByTransfer = 'subscription-by-transfer',
  RedemptionByTransfer = 'redemption-by-transfer',
  ScripDividend = 'scrip-dividend',
  CapitalIncrease = 'capital-increase',
  EquityRights = 'equity-rights',
  EquityFee = 'equity-fee',
  EquityAdjustment = 'equity-adjustment',
}

export const bankTransactionSubtypeSchema = z.enum(BankTransactionSubtype);

/**
 * Single account movement (flat row — not grouped like accounts or positions).
 *
 * @remarks
 * UI form row (camelCase). Maps 1:1 to `AdvisoryAccountsCitecTransaction` via
 * `buildBulkUploadAccountData` in
 * `@client/banks/utils/build-bulk-upload-account-data`. `transactionDate` cannot be
 * after the current date-time.
 * @example
 *
 * ```ts
 * const transaction = bankTransactionSchema.parse({
 *   iban: 'ES9121000418450200051332',
 *   transactionDate: '2024-06-01T14:30:00',
 *   amount: -120.5,
 *   currency: 'EUR',
 *   description: 'Card payment',
 *   type: BankTransactionType.Account,
 *   subtype: BankTransactionSubtype.Food,
 * });
 * ```
 */
export const bankTransactionSchema = z.object({
  iban: optionalBankIbanSchema,
  transactionDate: z
    .string()
    .min(1)
    .check(({ value, issues }) => {
      const parsed = dayjs(value);

      if (parsed.isValid() && parsed.isAfter(dayjs())) {
        issues.push({
          code: 'custom',
          input: value,
          message: 'Date cannot be in the future',
        });
      }
    }),
  amount: z.number(),
  currency: z.string().min(1),
  name: z.string().optional(),
  isin: z.string().optional(),
  quantity: z.number().optional(),
  price: z.number().optional(),
  type: bankTransactionTypeSchema.optional(),
  assetType: bankPositionAssetTypeSchema.optional(),
  subtype: bankTransactionSubtypeSchema.optional(),
  description: z.string().trim().min(1),
});

export type BankTransaction = z.infer<typeof bankTransactionSchema>;

/** Transaction row targeted by the manual bank update drawer. */
export const updateBankTransactionSchema = bankTransactionSchema.extend({
  id: z.string().min(1),
});

export type UpdateBankTransaction = z.infer<typeof updateBankTransactionSchema>;
